// ===================================================================
// src/app/lib/notificationAlerts.ts
//
// Prioridade + som das notificações em tempo real (notificationSocket.ts).
//
//   urgent  → toast que só some ao clicar/fechar, som marcante, notificação
//             nativa do SO se a aba estiver em background
//   normal  → toast comum + som curto e suave (+ nativa em background)
//   silent  → só o toast de sempre, sem som
//
// Som sintetizado via Web Audio (sem arquivo de áudio no bundle). O
// navegador só libera áudio depois de um gesto do usuário na página —
// installAudioUnlock() escuta o primeiro clique/tecla e destrava o
// AudioContext; antes disso playNotificationSound() simplesmente não toca.
//
// Várias abas abertas: só UMA toca o som / mostra a notificação nativa
// (claimNotification — trava curta em localStorage, aba visível tem
// preferência). O toast aparece em todas, já que é UI de cada aba.
//
// Mudo é por dispositivo (localStorage), não por usuário: o caso real é o
// computador da recepção x quem usa fone — quem decide é a máquina.
// ===================================================================

import { useSyncExternalStore } from 'react';
import type { NotificationType } from '../services/notifications';

export type NotificationPriority = 'urgent' | 'normal' | 'silent';

const PRIORITY_BY_TYPE: Partial<Record<NotificationType, NotificationPriority>> = {
  card_overdue: 'urgent',
  session_forwarded_to_sector: 'urgent',
  session_needs_human: 'urgent',
  // Regra de etiqueta ("avisar") — o gestor escolheu ser avisado já.
  session_tag_alert: 'urgent',
  // Caso crítico para a coordenação: prioridade alta/urgente ou conversa
  // perto de estourar a meta (api: services/critical_alerts.py).
  critical_alert: 'urgent',
  // Mensagem agendada: família respondeu / conversa encerrada / falhou.
  scheduled_message_alert: 'normal',
  card_created_in_sector: 'normal',
  card_due_soon: 'normal',
};

export function getNotificationPriority(type: NotificationType): NotificationPriority {
  return PRIORITY_BY_TYPE[type] ?? 'silent';
}

// ── Estado observável (mudo + áudio destravado) ────────────────────────

const MUTED_KEY = 'sophia:notif-sound-muted';

const listeners = new Set<() => void>();
function emitChange() {
  listeners.forEach((l) => l());
}
function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function readMuted(): boolean {
  try {
    return localStorage.getItem(MUTED_KEY) === '1';
  } catch {
    return false;
  }
}

let muted = readMuted();

// Silenciar numa aba vale pras outras abas abertas da mesma máquina.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== MUTED_KEY) return;
    muted = readMuted();
    emitChange();
  });
}

export function setSoundMuted(value: boolean) {
  muted = value;
  try {
    if (value) localStorage.setItem(MUTED_KEY, '1');
    else localStorage.removeItem(MUTED_KEY);
  } catch {
    // sem storage — vale só pra esta aba
  }
  emitChange();
}

// ── Áudio ──────────────────────────────────────────────────────────────

let audioCtx: AudioContext | null = null;

function isAudioReady(): boolean {
  return audioCtx?.state === 'running';
}

/** Cria/retoma o AudioContext. Só funciona dentro de um gesto do usuário. */
export function unlockAudio(): Promise<boolean> {
  try {
    if (!audioCtx) {
      const Ctor = window.AudioContext
        ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return Promise.resolve(false);
      audioCtx = new Ctor();
      audioCtx.onstatechange = emitChange;
    }
    if (audioCtx.state === 'running') return Promise.resolve(true);
    return audioCtx.resume().then(() => {
      emitChange();
      return isAudioReady();
    }).catch(() => false);
  } catch {
    return Promise.resolve(false);
  }
}

/** Destrava o áudio no primeiro clique/tecla da sessão. Retorna o cleanup. */
export function installAudioUnlock(): () => void {
  const events = ['pointerdown', 'keydown'] as const;
  const handler = () => {
    unlockAudio().then((ok) => {
      if (ok) events.forEach((e) => window.removeEventListener(e, handler));
    });
  };
  events.forEach((e) => window.addEventListener(e, handler));
  return () => events.forEach((e) => window.removeEventListener(e, handler));
}

function beep(ctx: AudioContext, freq: number, start: number, duration: number, volume: number) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(volume, start + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  osc.connect(gain).connect(ctx.destination);
  osc.start(start);
  osc.stop(start + duration + 0.02);
}

/** Toca o som da prioridade. Não toca se mudo, se 'silent' ou se o áudio ainda não foi destravado. */
export function playNotificationSound(priority: NotificationPriority, { force = false } = {}) {
  if (priority === 'silent' || (muted && !force) || !audioCtx || !isAudioReady()) return;
  const t = audioCtx.currentTime;
  if (priority === 'urgent') {
    // Dois "ding-dongs" — perceptível mesmo com a pessoa longe da tela.
    beep(audioCtx, 988, t, 0.22, 0.28);
    beep(audioCtx, 1319, t + 0.16, 0.32, 0.28);
    beep(audioCtx, 988, t + 0.55, 0.22, 0.28);
    beep(audioCtx, 1319, t + 0.71, 0.4, 0.28);
  } else {
    beep(audioCtx, 784, t, 0.14, 0.12);
    beep(audioCtx, 1047, t + 0.1, 0.22, 0.12);
  }
}

// ── Uma aba só toca ────────────────────────────────────────────────────

const TAB_ID = Math.random().toString(36).slice(2);
const CLAIM_PREFIX = 'sophia:notif-claim:';

/**
 * Resolve true para exatamente uma aba por notificação (best-effort). Aba
 * visível tenta primeiro; abas em background esperam um pouco, então se
 * houver uma aba na frente é ela quem toca.
 */
export function claimNotification(notificationId: string): Promise<boolean> {
  const key = CLAIM_PREFIX + notificationId;
  const delay = document.hidden ? 150 : 0;
  return new Promise((resolve) => {
    setTimeout(() => {
      try {
        if (localStorage.getItem(key)) return resolve(false);
        localStorage.setItem(key, TAB_ID);
      } catch {
        return resolve(true); // sem storage, sem coordenação — toca
      }
      setTimeout(() => {
        try {
          const won = localStorage.getItem(key) === TAB_ID;
          if (won) setTimeout(() => { try { localStorage.removeItem(key); } catch { /* ignora */ } }, 15_000);
          resolve(won);
        } catch {
          resolve(true);
        }
      }, 60);
    }, delay);
  });
}

// ── Hook pro botão de som do TopBar ────────────────────────────────────

interface SoundState {
  muted: boolean;
  /** true enquanto o navegador ainda não deixou tocar (falta um clique na página). */
  blocked: boolean;
}

let cachedState: SoundState = { muted, blocked: !isAudioReady() };
function getSnapshot(): SoundState {
  const blocked = !isAudioReady();
  if (cachedState.muted !== muted || cachedState.blocked !== blocked) {
    cachedState = { muted, blocked };
  }
  return cachedState;
}

export function useNotificationSound(): SoundState {
  return useSyncExternalStore(subscribe, getSnapshot);
}
